import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import Layout from "./Layout";
import LeaveTypeView from "./LeaveManagement/LeaveTypeView";
import { leaveService } from "../../api/LeaveService/leaveService";

const LeaveTypeTable = () => {
  const [showModal, setShowModal] = useState(false);
  const [selectedLeaveType, setSelectedLeaveType] = useState(null); // Added state
  const queryClient = useQueryClient();

  // Fetching data
  const { data: leaveTypes = [], isLoading } = useQuery({
    queryKey: ["leaveTypes"],
    queryFn: leaveService.getLeaveTypes,
  });

  // Mutation for deleting
  const deleteMutation = useMutation({
    mutationFn: leaveService.deleteLeaveType,
    onSuccess: () => {
      queryClient.invalidateQueries(["leaveTypes"]);
      toast.success("Leave type deleted successfully!");
    },
  });

  // Function to handle Edit click
  const handleEditClick = (leaveType) => {
    setSelectedLeaveType(leaveType); // Pass the data to state
    setShowModal(true);              // Open the modal
  };

  // Function to handle Add click
  const handleAddNew = () => {
    setSelectedLeaveType(null); // Clear state for new item
    setShowModal(true);
  };

  return (
    <Layout>
      <div className="w-100 position-relative">
        <style>{`
          .custom-table-header th { 
            background-color: #1e293b !important; color: #ffffff !important; 
            font-size: 0.7rem !important; font-weight: 700 !important; 
            letter-spacing: 0.05em !important; text-transform: uppercase !important;
          }
          .table-cell-text { font-size: 0.8rem !important; color: #334155; }
        `}</style>

        {/* Header Section */}
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <h1 className="fw-bold text-dark m-0" style={{ letterSpacing: "-1.5px" }}>
              Leave Type Configurations
            </h1>
            <p className="text-muted small mb-0">Define and manage company leave policies</p>
          </div>
          <button
            className="btn btn-primary px-4 border-0"
            onClick={handleAddNew} // Updated to use helper function
            style={{ borderRadius: "8px", backgroundColor: "#0f172a", fontWeight: "600", fontSize: "0.85rem" }}
          >
            <i className="bi bi-plus-circle me-2"></i>Add New Leave Type
          </button>
        </div>

        {/* Table Section */}
        <div style={{ backgroundColor: "rgba(255, 255, 255, 0.6)", backdropFilter: "blur(12px)", borderRadius: "16px", border: "1px solid rgba(255, 255, 255, 0.3)", overflow: "hidden" }}>
          <div className="table-responsive">
            <table className="table table-hover mb-0">
              <thead className="custom-table-header">
                <tr>
                  <th className="py-3 ps-4 border-0">LEAVE NAME</th>
                  <th className="py-3 border-0">DESCRIPTION</th>
                  <th className="py-3 border-0">CREATED AT</th>
                  <th className="py-3 border-0">UPDATED AT</th>
                  <th className="py-3 pe-4 border-0 text-end">ACTIONS</th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr>
                    <td colSpan="5" className="text-center py-4">Loading...</td>
                  </tr>
                ) : (
                  leaveTypes.map((lt) => (
                    <tr key={lt.id} className="align-middle">
                      <td className="ps-4 fw-bold table-cell-text">{lt.name}</td>
                      <td className="table-cell-text">{lt.description}</td>
                      <td className="table-cell-text">{new Date(lt.createdAt).toLocaleDateString()}</td>
                      <td className="table-cell-text">{new Date(lt.updatedAt).toLocaleDateString()}</td>
                      <td className="text-end pe-4">
                        <button className="btn btn-sm btn-outline-primary me-2" onClick={() => handleEditClick(lt)}>
                          <i className="bi bi-pencil"></i>
                        </button>
                        <button className="btn btn-sm btn-outline-danger" onClick={() => deleteMutation.mutate(lt.id)}>
                          <i className="bi bi-trash"></i>
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Modal: Pass the selectedLeaveType so the modal knows what to edit */}
        {showModal && (
          <LeaveTypeView 
            onClose={() => setShowModal(false)} 
            leaveType={selectedLeaveType} 
          />
        )}
      </div>
    </Layout>
  );
};

export default LeaveTypeTable;